/**
 * TaskListDetailPage — /c/:chapterSlug/m/tasks/:listId
 *
 * Standalone view for a single TaskList. Two layouts:
 *   - Kanban: 4 columns (Open / In progress / Blocked / Done), each
 *     task a draggable card. Dropping into a column PATCHes the task
 *     status to that column.
 *   - Table: tabular rows with title / status / assignee / due date
 *     plus inline Edit + Delete actions.
 *
 * Both views share the TaskEditModal and the same data source. The
 * list name is editable inline. The list itself can be deleted from
 * the page header.
 */

import { useCallback, useEffect, useState, type DragEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  ApiClientError,
  groupTasksApi,
  type GroupTask,
  type GroupTaskList,
  type GroupTaskStatus,
} from '@/services/api';
import {
  TaskEditModal,
  TASK_STATUS_LABEL,
  TASK_STATUS_PILL,
} from '@/components/member/TaskEditModal';

const STATUS_ORDER: GroupTaskStatus[] = ['open', 'in_progress', 'blocked', 'done'];

type ViewMode = 'kanban' | 'table';

export function TaskListDetailPage() {
  const params = useParams<{ chapterSlug: string; listId: string }>();
  const navigate = useNavigate();
  const listId = params.listId!;
  const chapterSlug = params.chapterSlug ?? '96';

  const [list, setList] = useState<(GroupTaskList & { groupName?: string }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>('kanban');
  const [editTask, setEditTask] = useState<GroupTask | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [pendingName, setPendingName] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await groupTasksApi.getList(listId);
      setList(d);
      setPendingName(d.name);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Failed to load list');
    } finally {
      setLoading(false);
    }
  }, [listId]);

  useEffect(() => { void reload(); }, [reload]);

  async function handleRename() {
    if (!list) return;
    const name = pendingName.trim();
    if (!name || name === list.name) { setRenaming(false); return; }
    setBusy(true);
    setError(null);
    try {
      await groupTasksApi.renameList(list.id, name);
      setRenaming(false);
      await reload();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Rename failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteList() {
    if (!list) return;
    if (!window.confirm(`Delete list "${list.name}" and all its tasks?`)) return;
    try {
      await groupTasksApi.deleteList(list.id);
      navigate(`/c/${chapterSlug}/m/groups`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  async function handleTaskStatusChange(taskId: string, status: GroupTaskStatus) {
    try {
      await groupTasksApi.updateTask(taskId, { status });
      await reload();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Update failed');
    }
  }

  async function handleDeleteTask(taskId: string, title: string) {
    if (!window.confirm(`Delete task "${title}"?`)) return;
    try {
      await groupTasksApi.deleteTask(taskId);
      await reload();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  if (loading) {
    return <div className="p-6 text-white/60">Loading…</div>;
  }
  if (!list) {
    return (
      <div className="p-6">
        <p className="text-red-400 mb-3">{error ?? 'List not found.'}</p>
        <Link to={`/c/${chapterSlug}/m/groups`} className="link text-sm">← Back to groups</Link>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-7xl mx-auto space-y-3">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="min-w-0">
          <Link to={`/c/${chapterSlug}/m/groups`} className="text-xs text-white/60 hover:text-white">
            ← Back to tasks
          </Link>
          {renaming ? (
            <form
              onSubmit={(e) => { e.preventDefault(); void handleRename(); }}
              className="flex items-center gap-2 mt-1"
            >
              <input
                className="input py-1 text-xl font-display flex-1"
                value={pendingName}
                autoFocus
                maxLength={200}
                onChange={(e) => setPendingName(e.target.value)}
                onBlur={() => void handleRename()}
              />
              <button type="submit" disabled={busy} className="btn-primary text-sm px-3 py-1">
                {busy ? '…' : 'Save'}
              </button>
              <button type="button" onClick={() => { setRenaming(false); setPendingName(list.name); }} className="text-xs text-white/60">Cancel</button>
            </form>
          ) : (
            <h1 className="text-2xl font-display font-bold text-white flex items-center gap-2 mt-1">
              <span className="truncate">{list.name}</span>
              <button
                type="button"
                onClick={() => setRenaming(true)}
                className="text-white/40 hover:text-white"
                aria-label="Rename list"
                title="Rename"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            </h1>
          )}
          {list.groupName && <p className="text-xs text-white/40">in {list.groupName}</p>}
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded border border-white/10 overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => setView('kanban')}
              className={`px-3 py-1.5 ${view === 'kanban' ? 'bg-primary/20 text-primary' : 'text-white/70 hover:bg-white/5'}`}
            >
              Kanban
            </button>
            <button
              type="button"
              onClick={() => setView('table')}
              className={`px-3 py-1.5 ${view === 'table' ? 'bg-primary/20 text-primary' : 'text-white/70 hover:bg-white/5'}`}
            >
              Table
            </button>
          </div>
          <button
            type="button"
            onClick={handleDeleteList}
            className="text-xs text-red-400 hover:text-red-300 px-2 py-1"
          >
            Delete list
          </button>
        </div>
      </header>

      {error && <p className="text-sm text-red-400">{error}</p>}

      {view === 'kanban' ? (
        <KanbanView
          tasks={list.tasks}
          onMoveTask={handleTaskStatusChange}
          onOpenTask={setEditTask}
        />
      ) : (
        <TableView
          tasks={list.tasks}
          onOpenTask={setEditTask}
          onDeleteTask={handleDeleteTask}
        />
      )}

      {editTask && (
        <TaskEditModal
          task={editTask}
          onClose={() => setEditTask(null)}
          onSaved={() => { setEditTask(null); void reload(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Kanban view — HTML5 DnD between status columns.
// ---------------------------------------------------------------------------

function KanbanView({
  tasks,
  onMoveTask,
  onOpenTask,
}: {
  tasks: GroupTask[];
  onMoveTask: (id: string, status: GroupTaskStatus) => void;
  onOpenTask: (t: GroupTask) => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overCol, setOverCol] = useState<GroupTaskStatus | null>(null);

  function handleDragStart(e: DragEvent<HTMLElement>, taskId: string) {
    setDragId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
  }
  function handleDragOver(e: DragEvent<HTMLElement>, status: GroupTaskStatus) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverCol(status);
  }
  function handleDrop(e: DragEvent<HTMLElement>, status: GroupTaskStatus) {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain') || dragId;
    if (id) {
      const moving = tasks.find((t) => t.id === id);
      if (moving && moving.status !== status) onMoveTask(id, status);
    }
    setDragId(null);
    setOverCol(null);
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
      {STATUS_ORDER.map((status) => {
        const colTasks = tasks.filter((t) => t.status === status);
        const isOver = overCol === status;
        return (
          <section
            key={status}
            onDragOver={(e) => handleDragOver(e, status)}
            onDragLeave={() => setOverCol(null)}
            onDrop={(e) => handleDrop(e, status)}
            className={`card flex flex-col min-h-[8rem] ${isOver ? 'ring-2 ring-primary/60' : ''}`}
          >
            <header className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-white/90">{TASK_STATUS_LABEL[status]}</h3>
              <span className="text-xs text-white/40">{colTasks.length}</span>
            </header>
            <ul className="space-y-1.5">
              {colTasks.length === 0 && (
                <li className="text-xs text-white/40 px-2 py-2">Drop tasks here.</li>
              )}
              {colTasks.map((t) => (
                <li key={t.id}>
                  <div
                    draggable
                    onDragStart={(e) => handleDragStart(e, t.id)}
                    onClick={() => onOpenTask(t)}
                    className="cursor-grab active:cursor-grabbing px-3 py-2 rounded bg-white/5 hover:bg-white/10 border border-white/10"
                    title="Drag to change status, or click to edit"
                  >
                    <p className="text-sm text-white font-medium truncate">{t.title}</p>
                    {t.assigneeName && (
                      <p className="text-[11px] text-white/50">👤 {t.assigneeName}</p>
                    )}
                    {t.dueDate && (
                      <p className="text-[11px] text-white/50">📅 {new Date(t.dueDate).toLocaleDateString()}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table view — sortable rows with edit/delete inline.
// ---------------------------------------------------------------------------

function TableView({
  tasks,
  onOpenTask,
  onDeleteTask,
}: {
  tasks: GroupTask[];
  onOpenTask: (t: GroupTask) => void;
  onDeleteTask: (id: string, title: string) => void;
}) {
  if (tasks.length === 0) {
    return (
      <div className="card text-center py-8 text-white/60">
        <p>No tasks in this list.</p>
      </div>
    );
  }
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-white/60 uppercase text-xs">
          <tr>
            <th className="px-2 py-2">Title</th>
            <th className="px-2 py-2">Status</th>
            <th className="px-2 py-2">Assignee</th>
            <th className="px-2 py-2">Due</th>
            <th className="px-2 py-2"></th>
          </tr>
        </thead>
        <tbody className="text-white/80">
          {tasks.map((t) => (
            <tr key={t.id} className="border-t border-white/10">
              <td className="px-2 py-2">
                <p className="text-white font-medium">{t.title}</p>
                {t.description && (
                  <p className="text-xs text-white/50 line-clamp-1 max-w-md">{t.description}</p>
                )}
              </td>
              <td className="px-2 py-2">
                <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${TASK_STATUS_PILL[t.status]}`}>
                  {TASK_STATUS_LABEL[t.status]}
                </span>
              </td>
              <td className="px-2 py-2">{t.assigneeName ?? '—'}</td>
              <td className="px-2 py-2">{t.dueDate ? new Date(t.dueDate).toLocaleDateString() : '—'}</td>
              <td className="px-2 py-2 text-right">
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => onOpenTask(t)}
                    className="text-xs px-2 py-1 bg-white/10 rounded hover:bg-white/20"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => onDeleteTask(t.id, t.title)}
                    className="text-xs px-2 py-1 bg-red-500/20 text-red-300 border border-red-500/30 rounded hover:bg-red-500/30"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default TaskListDetailPage;
